import { useEffect, useId, useRef, useState } from 'react';
import { useAudio, useConfig, useMachineState, useRobot, useRobotStatus } from '../app/context';
import { formatDuration } from '../domain/format';
import type { Screen } from '../domain/types';
import { useSecondsUntil } from '../hooks/useSecondsUntil';
import { ApiRequestError } from '../services/api';
import type { LampCommand } from '../services/robot';
import { Button } from './Button';
import { cx } from './cx';
import { HazardFrame } from './HazardFrame';
import styles from './LampControl.module.css';

const DURATIONS = [15, 30, 60, 120, 300] as const;

/** The cycle screens own the lamp; manual control steps aside while they run. */
const isCycleScreen = (screen: Screen) => screen === 'countdown' || screen === 'cycle';

/** Top-bar indicator of the relay state; opens the lamp panel. */
export function LampPill({ onOpen }: { readonly onOpen: () => void }) {
  const { lampOn, manualAutoOffAt } = useRobotStatus();
  const { screen } = useMachineState();
  const secondsLeft = useSecondsUntil(manualAutoOffAt);

  const label =
    lampOn === null
      ? 'LAMP —'
      : lampOn
        ? secondsLeft !== null
          ? `LAMP TEST · ${formatDuration(secondsLeft)}`
          : 'LAMP ON'
        : 'LAMP OFF';

  return (
    <button
      type="button"
      className={cx(styles.pill, lampOn === true && styles.pillOn)}
      onClick={onOpen}
      disabled={isCycleScreen(screen)}
      aria-haspopup="dialog"
      aria-label={`${label}. Open lamp control`}
    >
      <span className={styles.pillDot} />
      {label}
    </button>
  );
}

/** Red stage border whenever the lamp is lit outside a cycle (the cycle screens draw their own). */
export function ManualLampHazard() {
  const { lampOn } = useRobotStatus();
  const { screen } = useMachineState();
  return lampOn === true && !isCycleScreen(screen) ? <HazardFrame tone="danger" /> : null;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    switch (error.code) {
      case 'unauthorized':
        return 'Sign in to switch the lamp on.';
      case 'cycle_in_progress':
        return 'A disinfection cycle is running on this unit.';
      case 'manual_limit_exceeded':
        return error.message;
      case 'lamp_unavailable':
        return 'The lamp did not respond. Check that the Shelly switch is online.';
      case 'lamp_not_configured':
        return 'No lamp relay is configured for this unit.';
      default:
        return error.message;
    }
  }
  return 'Cannot reach the control service. Check the connection.';
}

/**
 * Manual lamp switch. "Off" is always available. "On" needs a signed-in operator, an explicit
 * empty-room confirmation, and a bounded duration — the relay switches itself off afterwards.
 */
export function LampControlPanel({ onClose }: { readonly onClose: () => void }) {
  const robot = useRobot();
  const audio = useAudio();
  const status = useRobotStatus();
  const { operator, screen, cycleTotal } = useMachineState();
  const { manualLampMaxSeconds } = useConfig();
  const secondsLeft = useSecondsUntil(status.manualAutoOffAt);

  const options = DURATIONS.filter((s) => s <= manualLampMaxSeconds);
  const [seconds, setSeconds] = useState<number>(options.includes(30) ? 30 : (options[0] ?? 15));
  const [roomConfirmed, setRoomConfirmed] = useState(false);
  const [pending, setPending] = useState<'on' | 'off' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const lampOn = status.lampOn === true;
  const signedIn = operator !== null;
  const blocked = isCycleScreen(screen);
  const canSwitchOn = signedIn && roomConfirmed && !blocked && !lampOn && pending === null;

  async function send(command: LampCommand) {
    setPending(command.on ? 'on' : 'off');
    setError(null);
    try {
      const result = await robot.setLamp(command);
      if (!result.acknowledged) {
        audio.play('alarm');
        setError('The lamp did not confirm it switched off. Isolate its power before entering.');
      } else {
        audio.play(command.on ? 'start' : 'cancel');
        setRoomConfirmed(false);
      }
    } catch (e) {
      audio.play('error');
      setError(errorMessage(e));
    } finally {
      setPending(null);
    }
  }

  return (
    <div className={styles.backdrop}>
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h3 id={titleId} className={styles.title}>
          Lamp Test
        </h3>
        <p className={styles.note}>
          Powers the machine briefly to check the bulbs light — from outside the room. A test does
          not disinfect or log the room: to disinfect, choose a ward and start a{' '}
          {formatDuration(cycleTotal)} cycle.
        </p>

        <div className={styles.readout} role="status">
          <span className={cx(styles.state, lampOn && styles.stateOn)}>
            {status.lampOn === null ? 'UNKNOWN' : lampOn ? 'ON' : 'OFF'}
          </span>
          {status.lampPowerW !== null && (
            <span className={styles.metric}>{Math.round(status.lampPowerW)} W</span>
          )}
          {lampOn && secondsLeft !== null && (
            <span className={styles.metric}>Auto-off in {formatDuration(secondsLeft)}</span>
          )}
          {!status.connected && <span className={styles.metric}>Controller offline</span>}
        </div>

        {!signedIn && (
          <p className={styles.note}>
            Sign in to switch the lamp on. Switching off is always available.
          </p>
        )}

        <fieldset className={styles.durations} role="radiogroup" disabled={!signedIn || lampOn}>
          <legend>SWITCH ON FOR</legend>
          {options.map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={s === seconds}
              className={styles.duration}
              onClick={() => setSeconds(s)}
              disabled={!signedIn || lampOn}
            >
              {formatDuration(s)}
            </button>
          ))}
        </fieldset>

        <label className={styles.confirm}>
          <input
            type="checkbox"
            checked={roomConfirmed}
            onChange={(e) => setRoomConfirmed(e.target.checked)}
            disabled={!signedIn || lampOn}
          />
          The room is empty and the door is closed
        </label>

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        <div className={styles.actions}>
          <Button variant="ghost" size="sm" className={styles.close} onClick={onClose}>
            CLOSE
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void send({ on: false })}
            disabled={pending !== null}
          >
            {pending === 'off' ? 'SWITCHING OFF…' : 'SWITCH OFF'}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => void send({ on: true, seconds })}
            disabled={!canSwitchOn}
          >
            {pending === 'on' ? 'SWITCHING ON…' : 'SWITCH ON'}
          </Button>
        </div>
      </div>
    </div>
  );
}
